import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Select, Store } from '@ngxs/store';
import { AllUsersState } from 'app/library/all-users/models/all-users.state';
import { SidenavService } from 'app/library/grow/services/sidenav.service';
import { IAddTeamMembersActionPayload } from 'app/library/team/models/add-team-members-action.action.payload';
import { IAddTeamWatchersActionPayload } from 'app/library/team/models/add-team-watchers-action.action.payload';
import { IRemoveTeamWatchersActionPayload } from 'app/library/team/models/remove-team-watchers-action.action.payload';
import { TeamBreadcrumb } from 'app/library/team/models/team-breadcrumb.model';
import { Team } from 'app/library/team/models/team.model';
import { UserTeamRoleEnum } from 'app/library/team/models/user-team-role.enum';
import { TeamNavigationService } from 'app/library/team/services/team-navigation.service';
import { TeamService } from 'app/library/team/services/team.service';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { Observable } from 'rxjs';
import { AddUserToTeamDialogComponent } from '../dialogs/add-user-to-team-dialog/add-user-to-team-dialog.component';

@Component({
    selector: 'app-team-card',
    templateUrl: './team-card.component.html',
    styleUrls: ['./team-card.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class TeamCardComponent extends UnsubscribeOnDestroy implements OnInit {
    @Select(AllUsersState.getEntities)
    filteredAllUsers$: Observable<User[]>;

    @Input()
    team: Team;

    /* Team Membership Card Styles */
    @Input()
    isTeamMembership: boolean;

    confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent>;
    addUserToTeamDialogRef: MatDialogRef<AddUserToTeamDialogComponent>;
    UserTeamRoleEnum = UserTeamRoleEnum;

    constructor(
        private _store: Store,
        private _router: Router,
        private _matDialog: MatDialog,
        private _translateService: TranslateService,
        private _matSnackBar: MatSnackBar,
        public sideNav: SidenavService,
        public teamService: TeamService,
        private _teamNavigationService: TeamNavigationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.team.breadcrumbs = this.generateBreadcrumbNames(this.team.breadcrumbs) as any;
    }

    generateBreadcrumbNames(breadcrumbs: TeamBreadcrumb[]): string {
        const breadcrumbNames = breadcrumbs.map((teamBreadcrumb, index) => {
            if (index === breadcrumbs.length - 1) {
                return teamBreadcrumb.name; // Last breadcrumb, don't add slash
            } else if (index === breadcrumbs.length - 2) {
                return teamBreadcrumb.name + ' / '; // Second-to-last breadcrumb, add slash after name
            } else {
                return teamBreadcrumb.name + ' / '; // Other breadcrumbs, add slash after name
            }
        });

        return breadcrumbNames.join('');
    }

    async getTeamWatchers(teamId: string, menuTrigger: MatMenuTrigger): Promise<void> {
        await this.teamService.fetchTeamWatchers(teamId);
        menuTrigger.openMenu();
    }

    openRemoveTeamWatcherDialog(teamWatcher: User, team: Team): void {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'GENERAL.BUTTON.CONFIRM';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant('TEAMS.DIALOG.REMOVE_TEAM_WATCHER.MESSAGE', {
            fullName: teamWatcher.fullName,
        });
        this.confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const payload: IRemoveTeamWatchersActionPayload = {
                    userIds: [teamWatcher.id],
                    teamId: team.id,
                };

                const isRemoved = await this.teamService.removeTeamWatchers(payload);
                if (isRemoved) {
                    team.watcherCount--;
                    const profile = this._store.selectSnapshot(UserState.getMyInfo);
                    const isCurrentUserTeamWatcher = payload.userIds.includes(profile.id);
                    if (isCurrentUserTeamWatcher) {
                        team.isCurrentUserWatcher = false;
                    }
                }
            }

            this.confirmDialogRef = null;
        });
    }

    async openAddUserToTeamDialog(team: Team, userTeamRole: UserTeamRoleEnum): Promise<void> {
        await this.teamService.fetchTeamLeaderTeams();

        let title = '';
        let role = '';
        let message = '';

        switch (userTeamRole) {
            case UserTeamRoleEnum.MEMBER:
                title = 'TEAMS.MEMBER.BUTTON.ADD_MEMBER';
                role = 'teamMember';
                message = 'TEAMS.DIALOG.ADD_USER_TO_TEAM.MEMBER.MESSAGE.SUCCESS';
                break;
            case UserTeamRoleEnum.WATCHER:
                title = 'TEAMS.WATCHER.BUTTON.ADD_WATCHER';
                role = 'teamWatcher';
                message = 'TEAMS.DIALOG.ADD_USER_TO_TEAM.WATCHER.MESSAGE.SUCCESS';
                break;
            default:
                break;
        }

        this.addUserToTeamDialogRef = this._matDialog.open(AddUserToTeamDialogComponent, {
            disableClose: false,
            width: '900px',
            autoFocus: false,
        });

        const teams = this.teamService.getTeamLeaderTeams();
        const clonedTeams = JSON.parse(JSON.stringify(teams));
        const teamListTree = this.teamService.convertListToTree(clonedTeams);

        this.addUserToTeamDialogRef.componentInstance.entities$ = this.filteredAllUsers$;
        this.addUserToTeamDialogRef.componentInstance.title = this._translateService.instant(title);
        this.addUserToTeamDialogRef.componentInstance.teamId = team.id;
        this.addUserToTeamDialogRef.componentInstance.role = role;
        this.addUserToTeamDialogRef.componentInstance.listTree = teamListTree;

        this.addUserToTeamDialogRef.afterClosed().subscribe(async (data: { selectedNodes: Set<User> }) => {
            if (data) {
                const userIds: string[] = Array.from(data.selectedNodes).map((user) => user.id);

                const payload: IAddTeamMembersActionPayload | IAddTeamWatchersActionPayload = {
                    userIds: userIds,
                    teamIds: [team.id],
                };

                let result = null;
                switch (userTeamRole) {
                    case UserTeamRoleEnum.MEMBER:
                        result = await this.teamService.addUsersToTeamAsMembers(payload);
                        this.teamService.fetchUserTeams(this._store.selectSnapshot(UserState.getMyInfo).id);
                        break;
                    case UserTeamRoleEnum.WATCHER:
                        result = await this.teamService.addUsersToTeamAsWatchers(payload);
                        if (result) {
                            this.team.watcherCount += userIds.length;
                            const profile = this._store.selectSnapshot(UserState.getMyInfo);
                            const isCurrentUserTeamWatcher = payload.userIds.includes(profile.id);
                            if (isCurrentUserTeamWatcher) {
                                team.isCurrentUserWatcher = true;
                            }
                        }
                        break;
                    default:
                        break;
                }

                if (
                    result &&
                    (userTeamRole !== UserTeamRoleEnum.MEMBER ||
                        (result as { asyncJobId?: string; teamMembers?: unknown[] }).asyncJobId ||
                        ((result as { teamMembers?: unknown[] }).teamMembers?.length ?? 0) > 0)
                ) {
                    const successMessage = this._translateService.instant(message);

                    this._matSnackBar.open(successMessage, 'OK', {
                        verticalPosition: 'top',
                        duration: SnackBarTime.medium,
                    });
                }
            }
            this.addUserToTeamDialogRef = null;
        });
    }

    async stopWatchingTeam(team: Team): Promise<void> {
        this.confirmDialogRef = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });

        this.confirmDialogRef.componentInstance.confirmTitle = 'TEAMS.WATCHER.DIALOG.STOP_WATCHING.TITLE';
        this.confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        this.confirmDialogRef.componentInstance.confirmMessage = 'TEAMS.WATCHER.DIALOG.STOP_WATCHING.MESSAGE';
        this.confirmDialogRef.componentInstance.actionButtonText = 'TEAMS.WATCHER.BUTTON.STOP_WATCHING';

        this.confirmDialogRef.afterClosed().subscribe(async (result) => {
            if (result) {
                const isRemoved = await this.teamService.stopWatchingTeam(team.id);
                if (isRemoved) {
                    team.watcherCount--;
                    team.isCurrentUserWatcher = false;
                    this._router.navigate(['/my-teams']);
                }

                this.confirmDialogRef = null;
            }
        });
    }

    navigateToTeamDetailsPage(teamId: string): void {
        if (!this.isTeamMembership) {
            this._teamNavigationService.addTeamPath(window.location.pathname);
            this._store.dispatch(new Navigate([`my-teams/team/${teamId}`]));
        }
    }
}
